'use client';

// АВТО-обновляет приложение при выходе новой сборки (поллинг /api/version + reload).
// INVARIANT(scriptera): service worker НЕ регистрировать (2026-07-03). Причина: его
// fetch-перехват вешал загрузки на iOS Safari на 10+ секунд, а «починенный» SW не
// долетал до телефонов — обновление SW само шло через зависший перехват. SW не нужен:
// установка на домашний экран работает без него (iOS всегда, Chrome — с 2024),
// авто-обновление — этот поллинг, офлайна и пушей нет. Канон: docs/HOW-IT-WORKS.md §PWA.
import { useEffect } from 'react';
import { nativeLayerActive, onLayersIdle } from '@/lib/native-layers';
import { AUTO_RELOAD_KEY, mayAutoReload } from '@/lib/auto-reload';
import { log, noteReload, previousReason, startJournal } from '@/lib/client-journal';
import { report, reportVersion, sendJournal, startAutoJournal } from '@/lib/client-report';
import { startWatch } from '@/lib/client-watch';
import { isNativeApp, nativeBridge, nativeMethod } from '@/lib/native-app';
import { hideJournalAllowed, handOffUpdate, pickUpdateAction, type UpdateBridgePort } from '@/lib/update-handoff';
import { fetchVersion } from '@/lib/version-poll';
import { startVersionLeader } from '@/lib/version-leader';

const CURRENT = process.env.NEXT_PUBLIC_BUILD_ID;
const POLL_MS = 60_000;

// ── Перезапуск на новую версию в приложении решает ОБОЛОЧКА (ТЗ релиза П3, 29.09.2026) ──
// Перезагрузка страницы под живыми родными экранами «накрывала» приложение до ручного
// убийства (31 % перезагрузок по журналам прода). Сборка с методом моста updateReady
// перезапускается сама, как на холодном старте; сайт в этом случае НЕ зовёт reload.
// Нет метода (браузер, старая сборка) или оболочка не взяла — прежняя перезагрузка.
function updatePort(): UpdateBridgePort | null {
  if (!nativeMethod('updateReady')) return null;
  const br = nativeBridge();
  if (!br?.updateReady) return null;
  return { has: true, call: (o) => br.updateReady!(o) };
}

/** Отдать перезапуск оболочке. true — взяла (страница не перезагружается), false — сами. */
async function nativeUpdate(reason: string, to: string, urgent: boolean): Promise<boolean> {
  const from = CURRENT ?? '';
  const handled = await handOffUpdate(updatePort(), { from, to, urgent });
  if (handled) {
    log('ver.native', { reason, from, to, urgent: urgent ? 1 : 0 });
    // Причина уезжает журналом следующей загрузки (boot:<reason>) — как у своей перезагрузки.
    noteReload(reason, { from, to, nativeReload: 1 });
  }
  return handled;
}

// ── Глобальный ловец крашей клиента (владелец 2026-07-21: «Application error» в
// приложении, консоли с телефона нет) ── НА УРОВНЕ МОДУЛЯ, не в эффекте: рендерный
// краш первой отрисовки не даёт эффектам выполниться, а модуль исполняется до рендера.
// Uncaught-исключения и unhandled-реджекты уходят в центр ошибок (/api/cabinet/ui-error,
// под сессией) со стеком и UA — смотрящий видит ТО ЖЕ, что упало у клиента.
// Кап 3 репорта на загрузку — от шторма повторов.
if (typeof window !== 'undefined') {
  // Журнал клиента и сторож поднимаются ЗДЕСЬ (владелец 25.08.2026): этот модуль —
  // единственный, который исполняется на КАЖДОЙ странице (mount в layout) и притом до
  // рендера. Своего места в разметке им не заводим — лишний компонент в layout ничего
  // не добавил бы, а стартовать надо раньше первой отрисовки.
  startJournal();
  startWatch();
  // ЖУРНАЛ ПРОШЛОЙ СТРАНИЦЫ УЕЗЖАЕТ САМ (владелец 01.09.2026: «обнаруживай такие вещи
  // сам»; с 06.09 — и в браузере, канон DIAGNOSTICS-CANON: «журнал везде»). Наша
  // перезагрузка (version/chunk/stale/hydration) или перезапуск приложения (app_restart,
  // хвост из localStorage) — через 4с после боота, чтобы захватить и первые события новой
  // страницы. Обычный заход без причины журнала не шлёт — нечего разбирать.
  const prevReason = previousReason();
  if (prevReason) {
    window.setTimeout(() => void sendJournal(`boot:${prevReason}`.slice(0, 32)), 4000);
  }
  // Уход родного экрана (чат, мастер, письмо, аккаунт, слои) — журнал с родным логом
  // уезжает сам, без кнопки (событие diagFlush оболочки, сборки с 06.09).
  startAutoJournal();
  // Версия этой страницы и приложения — в журнал версий по пользователю (П139).
  reportVersion();
  // Сам репортер живёт в общем модуле (lib/client-report): его зовут и отсюда, и сторож,
  // и «Диагностика» в настройках. Кап 3 на загрузку остался при нём.

  // ── Прожектор гидрации (2026-08-15): #418 в проде минифицирован и не говорит, ГДЕ
  // разметка разошлась (инцидент 355a478f: ~400 повторов, воспроизвести не вышло).
  // Наблюдатель стартует ДО гидрации (модульный код исполняется раньше рендера, см.
  // комментарий выше) и копит первые правки DOM: при расхождении React перерисовывает
  // поддерево на клиенте, и эти замены попадают сюда — old→new и есть виновник.
  // Штатная гидрация DOM не трогает — у чистой страницы лог пуст. Живёт 8 секунд,
  // при ошибке лог уезжает в context.hydro; издержки — только на первой загрузке.
  const HYDRO_CAP = 40;
  const hydroLog: string[] = [];
  let hydroObs: MutationObserver | null = null;
  const hydroStop = (): void => {
    hydroObs?.disconnect();
    hydroObs = null;
  };
  const nodePath = (n: Node | null): string => {
    const parts: string[] = [];
    for (let el = n instanceof Element ? n : n?.parentElement; el && parts.length < 5; el = el.parentElement) {
      const cls = typeof el.className === 'string' && el.className ? '.' + el.className.split(/\s+/, 2).join('.') : '';
      parts.unshift(el.tagName.toLowerCase() + (el.id ? '#' + el.id : cls));
    }
    return parts.join('>');
  };
  const short = (v: string | null | undefined): string => (v ?? '').replace(/\s+/g, ' ').slice(0, 90);
  // Тег с id и первыми классами: по «<div>» виновника не узнать, по
  // «<div#grammarly-…>» — сразу (чужой код в странице, см. foreignBodyNodes).
  const nodeDesc = (n: Node): string => {
    if (!(n instanceof Element)) return short(n.textContent);
    const cls = typeof n.className === 'string' && n.className ? '.' + n.className.split(/\s+/, 2).join('.') : '';
    return `<${n.tagName.toLowerCase()}${n.id ? '#' + n.id : cls}>${short(n.textContent)}`;
  };
  // Чужие узлы в <body>: React при провале гидрации сносит детей body и рисует своё
  // заново, так что в логе «del html>body» больше, чем «add html>body» (объявитель
  // маршрута Next добавляется всегда и не в счёт). Столько лишнего в body кладут
  // расширения браузера (переводчики, менеджеры паролей) ДО гидрации — это их разъезд,
  // не наш (инцидент 82999c1c, 12.09.2026: три «del <div>» против двух «add»). React
  // сам называет расширения первой причиной #418.
  // ПЕРВОЕ МЕСТО РАСХОЖДЕНИЯ (инциденты справки 03.10.2026: четыре #418 в Safari iOS,
  // лог показывал только «body снесён и нарисован заново»). Выброшенное серверное
  // поддерево body и его клиентскую перерисовку держим ссылками и на ошибке обходим
  // параллельно: первый узел, где тег/атрибуты/текст разные, и есть виновник.
  // Комментарии (маркеры React) пропускаем — клиентская отрисовка их не создаёт; эффекты
  // за 300 мс паузы могут добавить ложные отличия, поэтому шлём до трёх мест.
  const hydroDel: Element[] = [];
  const hydroAdd: Element[] = [];
  const kids = (n: Node): Node[] =>
    Array.from(n.childNodes).filter((c) => c.nodeType === 1 || (c.nodeType === 3 && (c.textContent ?? '') !== ''));
  const attrsOf = (el: Element): string =>
    Array.from(el.attributes)
      .map((a) => (a.name === 'style' ? `style=${(el as HTMLElement).style.cssText}` : `${a.name}=${a.value}`))
      .sort()
      .join(' ');
  const treeDiff = (s: Node, c: Node, out: string[]): void => {
    if (out.length >= 3) return;
    if (s.nodeType !== c.nodeType || s.nodeName !== c.nodeName) {
      out.push(`${nodePath(s.parentNode)}: сервер ${nodeDesc(s)} ≠ клиент ${nodeDesc(c)}`);
      return;
    }
    if (s.nodeType === 3) {
      if (s.textContent !== c.textContent) out.push(`${nodePath(s.parentNode)}: текст «${short(s.textContent)}» ≠ «${short(c.textContent)}»`);
      return;
    }
    const sa = attrsOf(s as Element);
    const ca = attrsOf(c as Element);
    if (sa !== ca) out.push(`${nodePath(s)}: атрибуты «${short(sa)}» ≠ «${short(ca)}»`);
    const sk = kids(s);
    const ck = kids(c);
    sk.forEach((sn, i) => {
      const cn = ck[i];
      if (cn && out.length < 3) treeDiff(sn, cn, out);
    });
    if (sk.length !== ck.length && out.length < 3) {
      const more = sk[ck.length] ?? ck[sk.length];
      const who = sk.length > ck.length ? 'сервера' : 'клиента';
      out.push(`${nodePath(s)}: детей ${sk.length} ≠ ${ck.length}, лишний у ${who} ${more ? nodeDesc(more) : ''}`);
    }
  };
  const hydroFirstDiff = (): string => {
    const out: string[] = [];
    const used = new Set<Element>();
    for (const s of hydroDel) {
      const c = hydroAdd.find((a) => !used.has(a) && a.tagName === s.tagName && a.className === s.className);
      if (!c) continue;
      used.add(c);
      treeDiff(s, c, out);
      if (out.length >= 3) break;
    }
    return out.length ? `diff ${out.join(' ; ')}` : '';
  };
  const foreignBodyNodes = (lines: readonly string[]): boolean => {
    const dels = lines.filter((l) => l.startsWith('del html>body: ')).length;
    const adds = lines.filter((l) => l.startsWith('add html>body: ') && !l.includes('<next-route-announcer')).length;
    return dels > adds;
  };
  try {
    const start = (): void => {
      if (!document.body || hydroObs) return;
      hydroObs = new MutationObserver((recs) => {
        for (const r of recs) {
          // Узлы body копим и после переполнения лога (их сравнит hydroFirstDiff);
          // скрипты потока Next не в счёт — их сервер дописывает в конец body сам.
          if (r.type === 'childList' && r.target === document.body) {
            for (const n of r.removedNodes) if (n instanceof Element && n.tagName !== 'SCRIPT' && hydroDel.length < 8) hydroDel.push(n);
            for (const n of r.addedNodes) if (n instanceof Element && n.tagName !== 'SCRIPT' && hydroAdd.length < 8) hydroAdd.push(n);
          }
          if (hydroLog.length >= HYDRO_CAP) continue;
          if (r.type === 'characterData') {
            hydroLog.push(`txt ${nodePath(r.target)}: «${short(r.oldValue)}»→«${short(r.target.textContent)}»`);
          } else if (r.type === 'attributes') {
            const el = r.target as Element;
            hydroLog.push(
              `attr ${nodePath(el)}[${r.attributeName}]: «${short(r.oldValue)}»→«${short(el.getAttribute(r.attributeName ?? ''))}»`,
            );
          } else {
            for (const n of r.removedNodes) hydroLog.push(`del ${nodePath(r.target)}: ${nodeDesc(n)}`);
            for (const n of r.addedNodes) hydroLog.push(`add ${nodePath(r.target)}: ${nodeDesc(n)}`);
          }
        }
      });
      hydroObs.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true,
        characterDataOldValue: true,
        attributes: true,
        attributeOldValue: true,
      });
      window.setTimeout(hydroStop, 8000);
    };
    if (document.body) start();
    else document.addEventListener('DOMContentLoaded', start, { once: true });
  } catch {
    /* прожектор — только диагностика, страницу не трогает */
  }
  // Разъезд разметки при гидрации (React #418/#419/#423/#425) — НЕ падение: React сам
  // перерисовывает поддеревье на клиенте и живёт дальше, а до нас это долетает как
  // «Uncaught Error» только потому, что React 19 отдаёт восстановимые ошибки через
  // reportError() — тот бьёт обычным событием error окна. Отделяем в свой вид: в центре
  // ошибок он записывается, но владельца ночью не будит (QUIET_KINDS в error-center).
  // Инциденты bc0519de/a1620c57 (2026-07-22…31): 8 ложных «крашей» на живом кабинете.
  const HYDRATION_RE = /Minified React error #(418|419|423|425)\b|Hydration failed|hydrating/i;
  // Обрыв чанка СТАРОЙ сборки после деплоя (приложение было открыто, файлы на сервере
  // уже новые): Next пытается догрузить кусок, получает 404/обрыв — до 05.08 это
  // роняло клиент в аварийный ре-рендер, тот стирал атрибуты <html>, и нативная шапка
  // до перезапуска приложения жила с чужими цветами (белый титул на белом, владелец
  // 2026-08-05, IMG_1287). Теперь такой обрыв — честная перезагрузка на свежую сборку
  // (кап раз в 30с — от петли, при открытом нативном слое ждём его закрытия: reload
  // под слоем вешает приложение, канон NATIVE-SLIDE §11).
  const CHUNK_RE =
    /ChunkLoadError|Loading chunk .+ failed|Loading CSS chunk|dynamically imported module|Importing a module script failed/i;
  // Мёртвая сборка БЕЗ обрыва чанка (владелец 28.08 «после катки виснет, перезапускаю»):
  // на старой странице после деплоя умирают Server Actions и RSC-навигация — клики
  // «ничего не делают». Такие отказы чинит та же одна перезагрузка, но ТОЛЬКО после
  // сверки: /api/version отдаёт другую сборку — значит дело в выкатке, а не в сети.
  const STALE_RE =
    /Failed to find Server Action|Server Action .{0,80}not found|Failed to fetch RSC|Connection closed/i;
  // Шум браузера, а не падение: «ResizeObserver loop …» — уведомление спецификации о
  // том, что перерасчёт размеров не уложился в один кадр (браузер просто переносит
  // остаток на следующий). Ничего не ломается, стека нет (context.stack пустой), но
  // Chrome бьёт им обычным событием error окна — и до 18.08 такой шум записывался как
  // client_crash и СЪЕДАЛ кап в 3 репорта на загрузку, из-за чего настоящий краш той
  // же страницы мог не доехать (инцидент 5c8b3b85, /app/bots/<id>). Не репортим вовсе.
  const NOISE_RE = /^ResizeObserver loop /i;
  // Чужой код в вкладке: расширения браузера (MetaMask и прочие кошельки/блокировщики)
  // впрыскивают свои скрипты в каждую страницу, и их падения долетают в наши слушатели
  // окна как свои. Узнаём по стеку из *extension:// или по имени расширения — такое не
  // репортим вовсе (инцидент f83aebf0 20.08: «Failed to connect to MetaMask» будил
  // владельца, хотя это кошелёк в его же браузере, а не Скриптера).
  const EXT_RE = /\bMetaMask\b|(?:chrome|moz|safari-web)-extension:\/\//i;
  const fromExtension = (message: string, stack?: string): boolean =>
    EXT_RE.test(message) || (!!stack && EXT_RE.test(stack));
  // Одна автоперезагрузка на все причины «страница сломалась сама» (обрыв чанка,
  // провал гидрации): общий кап раз в 30с (mayAutoReload, метка в sessionStorage) —
  // от петли «упало → перезагрузились → упало снова».
  // reason — ТОЛЬКО для журнала (chunk | hydration): поведение перезагрузок не меняется,
  // но без записанной причины разобрать «после выкатки приложение тупит» нечем — после
  // reload память вкладки пуста (владелец 25.08.2026).
  const selfHealReload = (reason: string, to = ''): void => {
    try {
      if (!mayAutoReload(sessionStorage.getItem(AUTO_RELOAD_KEY), Date.now())) return;
      sessionStorage.setItem(AUTO_RELOAD_KEY, String(Date.now()));
    } catch {
      /* приватный режим — перезагружаемся без стража повторов */
    }
    const go = () => {
      noteReload(reason);
      window.location.reload();
    };
    const reloadSelf = () => {
      if (nativeLayerActive()) onLayersIdle(go);
      else go();
    };
    // Приложение с updateReady: страница уже сломана — перезапуск срочный, делает оболочка.
    if (pickUpdateAction(updatePort()) === 'native') {
      void nativeUpdate(reason, to, true).then((handled) => {
        if (!handled) reloadSelf();
      });
      return;
    }
    reloadSelf();
  };
  // Отказ, похожий на мёртвую сборку: перезагружаемся только если сборка на сервере
  // ДЕЙСТВИТЕЛЬНО другая — сетевой сбой с тем же билдом перезагрузкой не лечится.
  const staleHeal = (message: string, stack?: string): void => {
    void fetchVersion()
      .then((j) => {
        if (j.version && CURRENT && j.version !== CURRENT) {
          report('client_stale_build', message, stack);
          selfHealReload('stale', j.version);
        } else {
          report('client_crash', message, stack);
        }
      })
      .catch(() => report('client_crash', message, stack));
  };
  // СТАРАЯ СБОРКА ПОСЛЕ КАТКИ — НЕ ИНЦИДЕНТ (20.10.2026, инциденты 2c438dc2 и 8efbf120: обрыв
  // чанка и разъезд гидрации через минуту после выкатки, в контексте — прошлая сборка). Файлов
  // старой сборки на сервере нет, HTML уже новый: страница обязана перезагрузиться, и она это
  // делает. Записывать это ошибкой значит заводить инцидент на каждую катку и гонять по нему
  // дежурного (2c438dc2 — семь возвратов). Сверяем сборку с /api/version: другая — только
  // строка журнала; та же (или сверка не удалась за STALE_CHECK_MS) — настоящая поломка, репорт
  // как раньше. Перезагрузка — ПОСЛЕ сверки в обоих случаях: репорт keepalive не переживает
  // отменённый запрос версии.
  const STALE_CHECK_MS = 3000;
  const afterStaleCheck = (reason: string, onReal: () => void, then: () => void): void => {
    let done = false;
    const finish = (stale: boolean): void => {
      if (done) return;
      done = true;
      if (stale) log(`${reason}.stale`, { from: CURRENT ?? 'dev' });
      else onReal();
      then();
    };
    window.setTimeout(() => finish(false), STALE_CHECK_MS);
    fetchVersion().then(
      (j) => finish(!!(j.version && CURRENT && j.version !== CURRENT)),
      () => finish(false),
    );
  };
  const describeEvent = (ev: Event): string => {
    const t = ev.target as { tagName?: string; currentSrc?: string; src?: string; url?: string; href?: string } | null;
    const tag = typeof t?.tagName === 'string' ? t.tagName : t?.constructor?.name ?? '';
    // У <link> адрес в href (стили сборки) — без него «Event(error) on LINK» не разобрать.
    const src = [t?.currentSrc, t?.src, t?.url, t?.href].find((v) => typeof v === 'string' && v) as string | undefined;
    return `Event(${ev.type})${tag ? ` on ${tag}` : ''}${src ? ` ${src.split('?')[0]!.slice(0, 200)}` : ''}`;
  };
  window.addEventListener('error', (e) => {
    const message = String(e.message ?? e.error ?? 'unknown');
    if (NOISE_RE.test(message)) return;
    if (fromExtension(message, e.error?.stack)) return;
    if (CHUNK_RE.test(message)) {
      afterStaleCheck('chunk', () => report('client_chunk_reload', message, e.error?.stack), () => selfHealReload('chunk'));
      return;
    }
    if (STALE_RE.test(message)) {
      staleHeal(message, e.error?.stack);
      return;
    }
    if (HYDRATION_RE.test(message)) {
      // Замены восстановительной перерисовки уже в логе (ошибка приходит после неё);
      // короткая пауза добирает хвост, потом лог уезжает вместе с репортом.
      window.setTimeout(() => {
        hydroStop();
        // Разъезд из-за чужих узлов в body (расширение браузера) — не наш, инцидента
        // нет; перезагрузка ниже остаётся: она дешёвая страховка обработчиков (Ф8.1).
        // Ф8.1 (владелец 18.08: кнопки умирают): после разъезда разметки React живёт
        // «наполовину» — обработчики повисают на выброшенном поддереве, и на живом
        // проде 18.08 это дало 465 client_hydration за сутки. Лечим тем же одним
        // reload, что и обрыв чанка: репорт уходит до него (afterStaleCheck), кап и
        // запрет под нативным слоем — внутри selfHealReload.
        if (foreignBodyNodes(hydroLog)) {
          log('hydration.foreign', { n: hydroLog.length });
          selfHealReload('hydration');
          return;
        }
        afterStaleCheck(
          'hydration',
          () =>
            report('client_hydration', message, e.error?.stack, {
              hydro:
                [hydroFirstDiff(), ...hydroLog].filter(Boolean).join(' | ').slice(0, 1400) ||
                '(лог правок DOM пуст)',
            }),
          () => selfHealReload('hydration'),
        );
      }, 300);
      return;
    }
    report('client_crash', message, e.error?.stack);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason as { message?: string; stack?: string } | undefined;
    // Промис отклонён СОБЫТИЕМ (img/video/EventSource/IndexedDB: `onerror = reject`) —
    // String(event) даёт «[object Event]», по нему разбирать нечего (инцидент 06d11147,
    // iOS /app/bots 15.09). Пишем тип события, тег цели и её адрес (без параметров).
    const message =
      typeof Event !== 'undefined' && e.reason instanceof Event
        ? describeEvent(e.reason)
        : String(r?.message ?? e.reason ?? 'unknown');
    if (fromExtension(message, r?.stack)) return;
    if (CHUNK_RE.test(message)) {
      afterStaleCheck('chunk', () => report('client_chunk_reload', message, r?.stack), () => selfHealReload('chunk'));
      return;
    }
    // Не загрузился файл стилей (<link>, React грузит стили сборки промисом) — тот же
    // признак мёртвой сборки, что обрыв чанка: после катки старых файлов на сервере нет.
    // Сборка та же (сеть) — staleHeal без перезагрузки пишет client_crash с адресом.
    if (STALE_RE.test(message) || /^Event\(error\) on LINK\b/.test(message)) {
      staleHeal(message, r?.stack);
      return;
    }
    report('client_unhandled_rejection', message, r?.stack);
  });
}

export function PwaUpdater() {
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    // КАПКАН (владелец 2026-07-18): раньше весь эффект был за guard'ом
    // «есть serviceWorker» — а в WKWebView iOS-приложения его НЕТ, и проверялка
    // версии не работала в приложении вовсе. SW-чистка — опциональна, поллинг — всем.
    if ('serviceWorker' in navigator) {
      // Активно СНОСИМ всё, что было зарегистрировано раньше: застрявшие старые SW
      // перестают перехватывать сеть после первой же удачной загрузки страницы.
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .catch(() => {});
    }

    let reloading = false;
    // Ждём ли мы закрытия нативных слоёв, чтобы свериться (одна подписка, не по тику).
    let idleWait = false;
    // Анти-флап при zero-downtime деплое: пока живы оба контейнера, /api/version чередует
    // старую и новую версии — не перезагружаемся на транзиентную версию.
    let pendingVersion: string | null = null;
    let pendingCount = 0;
    // Общий запрос с гейтом «Обновите приложение» (lib/version-poll): сервер один раз.
    const serverVersion = async (): Promise<string | null> => (await fetchVersion()).version;
    // Новая версия подтверждена: в приложении с updateReady перезапуск делает оболочка,
    // иначе — прежняя перезагрузка страницы.
    const applyVersion = async (to: string): Promise<void> => {
      reloading = true;
      if (await nativeUpdate('version', to, false)) return;
      noteReload('version', { from: CURRENT, to });
      window.location.reload();
    };
    // immediate — интерактивный триггер (тап по нативной вкладке / возврат в приложение):
    // подтверждаем новую версию ВТОРЫМ запросом сразу же и перезагружаемся, не дожидаясь
    // следующего тика (владелец 2026-07-20: обновляться прямо на тыке по меню). Двойной
    // запрос сохраняет защиту от флапа деплоя. Фоновый опрос (immediate=false) — прежняя
    // защита: одна и та же новая версия дважды подряд.
    // fromPoll — минутный тик лидера (lib/version-leader.ts): только он рассылает итог остальным
    // вкладкам. Сверки по событиям вкладки (старт, возврат, pageshow, повод от лидера) итог не
    // рассылают — иначе вкладки будили бы друг друга по кругу, пока версия новая.
    async function checkVersion(immediate = false, fromPoll = false) {
      if (reloading || !CURRENT) return;
      // INVARIANT(scriptera): НЕ перезагружаться, пока открыт/едет нативный слой
      // приложения. Причина: стопку слоёв ведут веб И натив параллельно; reload
      // стирает веб-половину, нативные карточки остаются — приложение виснет
      // (владелец 2026-07-21). Обновление просто откладывается до закрытия слоёв
      // (следующий тик/тык). Канон: docs/NATIVE-SLIDE-PLAN.md §11.
      // Путь с updateReady слои не ждёт: момент перезапуска выбирает сама оболочка.
      if (nativeLayerActive() && !nativeMethod('updateReady')) {
        log('ver.defer', { imm: immediate ? 1 : 0 });
        // Не бросаем обновление насовсем: как только слои закроются — сверимся снова
        // (владелец 28.08: мёртвая сборка жила, пока слой не пропустит все тики).
        // Подписка одноразовая и одна: onLayersIdle — постоянный список слушателей.
        if (!idleWait) {
          idleWait = true;
          const off = onLayersIdle(() => {
            off();
            idleWait = false;
            void checkVersion(true);
          });
        }
        return;
      }
      try {
        const v = await serverVersion();
        if (fromPoll) leader.share(v);
        if (!v || v === CURRENT) {
          pendingVersion = null;
          pendingCount = 0;
          return;
        }
        log('ver.new', { cur: CURRENT, srv: v, imm: immediate ? 1 : 0 });
        if (immediate) {
          const v2 = await serverVersion();
          if (v2 === v && v2 !== CURRENT) await applyVersion(v2);
          return;
        }
        pendingCount = v === pendingVersion ? pendingCount + 1 : 1;
        pendingVersion = v;
        if (pendingCount >= 2) {
          await applyVersion(v);
        } else {
          // Подтверждение — через 3с, не следующим минутным тиком (владелец 28.08:
          // катка при открытом приложении оставляла мёртвую сборку жить до 2 минут).
          // Флап зеро-даунтайм выкатки (живут оба контейнера) длится секунды — двойная
          // сверка с паузой 3с его всё так же отсеивает.
          window.setTimeout(() => void checkVersion(), 3000);
        }
      } catch {
        /* сеть недоступна — попробуем позже */
      }
    }

    // Журнал страницы уезжает при сворачивании приложения (П3): убийство приложения
    // свайпом иначе стирает хвост событий вместе с вкладкой. Кап — раз в 60 с.
    let lastHideJournal = 0;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion(true);
        return;
      }
      if (document.visibilityState === 'hidden' && isNativeApp()) {
        const now = Date.now();
        if (!hideJournalAllowed(lastHideJournal, now)) return;
        lastHideJournal = now;
        void sendJournal('hide');
      }
    };
    // СВЕРКА ВЕРСИИ НА КАЖДЫЙ ТАП ВКЛАДКИ СНЯТА (ТЗ WEB-CAUSES М42 п.2, владелец 05.10.2026:
    // «тормоз при переходе между вкладками, как будто дожидаемся ответа сервера»). Решение
    // 18.07 («новая версия подъезжает прямо на тыке» — слушатель события scr-nav из navTo)
    // отменено: запрос /api/version уходил на КАЖДОЕ переключение раздела в самый момент
    // хода. Свежая сборка по-прежнему подъезжает по интервалу POLL_MS, возврату в вкладку
    // (visibilitychange) и оживлению из кэша навигации (pageshow) — этого хватает.
    // pageshow с persisted: WebKit поднял страницу из кэша навигации — сборка могла
    // устареть, пока страница спала.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void checkVersion(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    // ОДИН ОПРОС НА БРАУЗЕР (ТЗ SPEED §8.1): минутный тик ведёт вкладка-лидер, остальные
    // получают итог через BroadcastChannel и сверяются сами, только если версия новая.
    const leader = startVersionLeader((v) => {
      if (v !== CURRENT) void checkVersion();
    });
    const id = window.setInterval(() => {
      if (leader.isLeader()) void checkVersion(false, true);
    }, POLL_MS);
    // Сразу на старте (владелец 2026-07-23): WebKit умеет поднять СТАРЫЙ бандл из кэша
    // после перезапуска приложения — раньше он жил до первого тика/тыка (до минуты) и
    // путал поведение свежего деплоя. Немедленная сверка (двойная, с защитой от флапа
    // деплоя) перезагружает на свежую сборку в первые секунды.
    void checkVersion(true);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
      window.clearInterval(id);
      leader.stop();
    };
  }, []);

  return null;
}
